import { SoftwareType } from "../types/software";
import { SoftwarePackage } from "../types/software";

export const SOFTWARE_TYPES: Record<
  SoftwareType,
  {
    name: string;
    description: string;
    techLevel: number;
    baseCost: number;
    properties: {
      bandwidth: number;
      effect: string;
      level?: number;
      crewReplacement?: number;
    };
  }
> = {
  manoeuvre: {
    name: "Manoeuvre",
    description: "Allows basic control of ship",
    techLevel: 8,
    baseCost: 0,
    properties: {
      bandwidth: 0,
      effect: "Basic ship control",
    },
  },
  intellect: {
    name: "Intellect",
    description: "Allows a ship to understand and obey verbal commands",
    techLevel: 11,
    baseCost: 0,
    properties: {
      bandwidth: 0,
      effect: "Voice command capability",
    },
  },
  jumpControl: {
    name: "Jump Control",
    description: "Controls jump drive operations",
    techLevel: 9,
    baseCost: 0.1,
    properties: {
      bandwidth: 5,
      effect: "Required for jump drive operation",
    },
  },
  evade: {
    name: "Evade",
    description: "Improves ship evasion",
    techLevel: 9,
    baseCost: 1,
    properties: {
      bandwidth: 10,
      effect: "Negative DM to incoming attacks",
    },
  },
  fireControl: {
    name: "Fire Control",
    description: "Improves weapon accuracy",
    techLevel: 9,
    baseCost: 2,
    properties: {
      bandwidth: 5,
      effect: "Automated weapon control",
    },
  },
  autoRepair: {
    name: "Auto Repair",
    description: "Automated repair systems",
    techLevel: 10,
    baseCost: 5,
    properties: {
      bandwidth: 10,
      effect: "Automated repair attempts",
    },
  },
  library: {
    name: "Library",
    description: "Ship's database and reference system",
    techLevel: 8,
    baseCost: 0,
    properties: {
      bandwidth: 0,
      effect: "Access to general knowledge",
    },
  },
  interface: {
    name: "Interface",
    description: "Basic user interface",
    techLevel: 7,
    baseCost: 0,
    properties: {
      bandwidth: 0,
      effect: "Standard interface",
    },
  },
  intelligentInterface: {
    name: "Intelligent Interface",
    description: "Advanced user interface",
    techLevel: 11,
    baseCost: 0.0001,
    properties: {
      bandwidth: 1,
      effect: "Enhanced interface",
    },
  },
  security: {
    name: "Security",
    description: "Basic security system",
    techLevel: 8,
    baseCost: 0,
    properties: {
      bandwidth: 0,
      effect: "Security protection",
    },
  },
  intrusion: {
    name: "Intrusion",
    description: "System intrusion capabilities",
    techLevel: 10,
    baseCost: 0.001,
    properties: {
      bandwidth: 1,
      effect: "Hacking assistance",
    },
  },
  expert: {
    name: "Expert",
    description: "Specialized knowledge system",
    techLevel: 11,
    baseCost: 0.001,
    properties: {
      bandwidth: 1,
      effect: "Skill simulation",
    },
  },
  translator: {
    name: "Translator",
    description: "Universal translation system",
    techLevel: 9,
    baseCost: 0.00005,
    properties: {
      bandwidth: 0,
      effect: "Language translation",
    },
  },
  database: {
    name: "Database",
    description: "Advanced information storage",
    techLevel: 7,
    baseCost: 0.00001,
    properties: {
      bandwidth: 0,
      effect: "Store and retrieve information",
      level: 0,
    },
  },
  agent: {
    name: "Agent",
    description: "Automated task management",
    techLevel: 11,
    baseCost: 0.0005,
    properties: {
      bandwidth: 0,
      effect: "Manage routine tasks",
    },
  },
  intellectAdvanced: {
    name: "Intellect Advanced",
    description: "Advanced AI with skill capabilities",
    techLevel: 12,
    baseCost: 0.002,
    properties: {
      bandwidth: 2,
      effect: "Advanced AI capabilities",
    },
  },
  advancedFireControl: {
    name: "Advanced Fire Control",
    description: "Enhanced weapon targeting",
    techLevel: 10,
    baseCost: 12,
    properties: {
      bandwidth: 15,
      effect: "+2 to attack rolls",
    },
  },
  antiHijack: {
    name: "Anti-Hijack",
    description: "Security system against unauthorized access",
    techLevel: 11,
    baseCost: 6,
    properties: {
      bandwidth: 2,
      effect: "+2 to security checks",
    },
  },
  battleNetwork: {
    name: "Battle Network",
    description: "Coordinates multiple weapon systems",
    techLevel: 12,
    baseCost: 5,
    properties: {
      bandwidth: 5,
      effect: "Coordinate multiple weapons",
    },
  },
  battleSystem: {
    name: "Battle System",
    description: "Comprehensive combat management",
    techLevel: 9,
    baseCost: 18,
    properties: {
      bandwidth: 5,
      effect: "+3 to combat checks",
    },
  },
  broadSpectrumEw: {
    name: "Broad Spectrum EW",
    description: "Electronic warfare suite",
    techLevel: 13,
    baseCost: 14,
    properties: {
      bandwidth: 12,
      effect: "+2 to electronic warfare",
    },
  },
  consciousIntelligence: {
    name: "Conscious Intelligence",
    description: "Advanced AI with self-awareness",
    techLevel: 16,
    baseCost: 25,
    properties: {
      bandwidth: 40,
      effect: "Full AI capabilities",
    },
  },
  electronicWarfare: {
    name: "Electronic Warfare",
    description: "Basic electronic warfare capabilities",
    techLevel: 10,
    baseCost: 15,
    properties: {
      bandwidth: 10,
      effect: "+1 to electronic warfare",
    },
  },
  launchSolution: {
    name: "Launch Solution",
    description: "Coordinates small craft launches",
    techLevel: 8,
    baseCost: 10,
    properties: {
      bandwidth: 5,
      effect: "+1 to launch checks",
    },
  },
  pointDefence: {
    name: "Point Defence",
    description: "Automated defensive systems",
    techLevel: 9,
    baseCost: 8,
    properties: {
      bandwidth: 12,
      effect: "Intercept incoming fire",
    },
  },
  screenOptimiser: {
    name: "Screen Optimiser",
    description: "Improves shield efficiency",
    techLevel: 10,
    baseCost: 5,
    properties: {
      bandwidth: 10,
      effect: "+1 to shield rating",
    },
  },
  virtualCrew: {
    name: "Virtual Crew",
    description: "Simulates additional crew members",
    techLevel: 10,
    baseCost: 1,
    properties: {
      bandwidth: 5,
      effect: "Replaces 1 crew member",
      crewReplacement: 1,
    },
  },
  virtualGunner: {
    name: "Virtual Gunner",
    description: "Automated weapon operation",
    techLevel: 9,
    baseCost: 1,
    properties: {
      bandwidth: 5,
      effect: "Operates one weapon system",
    },
  },
} as const;

export const calculateSoftwareCost = (
  type: SoftwareType,
  computerRating: number,
): number => {
  const software = SOFTWARE_TYPES[type];
  if (!software) return 0;

  // Base cost is multiplied by computer rating
  return software.baseCost * computerRating;
};

export const SOFTWARE_PACKAGES: SoftwarePackage[] = [
  // Basic Ship Software
  {
    id: "manoeuvre",
    name: "Manoeuvre",
    category: "Basic",
    tl: 8,
    bandwidth: 0,
    cost: 0,
    description: "Allows basic control of ship.",
  },
  {
    id: "intellect",
    name: "Intellect",
    category: "Basic",
    tl: 11,
    bandwidth: 0,
    cost: 0,
    description: "Allows a ship to understand and obey verbal commands.",
  },
  // Jump Control with proper levels
  {
    id: "jumpControl",
    name: "Jump Control",
    category: "Navigation",
    tl: 9,
    bandwidth: 5,
    cost: 0.1,
    description:
      "Allows jumps of up to the specified number. Incorporates astrogation software and jump engine management.",
    maxLevel: 6,
    levels: [
      { level: 1, bandwidth: 5, cost: 0.1, tl: 9 },
      { level: 2, bandwidth: 10, cost: 0.2, tl: 11 },
      { level: 3, bandwidth: 15, cost: 0.3, tl: 12 },
      { level: 4, bandwidth: 20, cost: 0.4, tl: 13 },
      { level: 5, bandwidth: 25, cost: 0.5, tl: 14 },
      { level: 6, bandwidth: 30, cost: 0.6, tl: 15 },
    ],
  },
  // Evade with proper levels
  {
    id: "evade",
    name: "Evade",
    category: "Combat",
    tl: 9,
    bandwidth: 10,
    cost: 1,
    description:
      "The computer reacts automatically to incoming fire, applying a negative DM equal to the Evade program's score to all attacks.",
    maxLevel: 3,
    levels: [
      { level: 1, bandwidth: 10, cost: 1, tl: 9 },
      { level: 2, bandwidth: 15, cost: 2, tl: 11 },
      { level: 3, bandwidth: 25, cost: 3, tl: 13 },
    ],
  },
  // Fire Control with proper levels
  {
    id: "fireControl",
    name: "Fire Control",
    category: "Combat",
    tl: 9,
    bandwidth: 5,
    cost: 2,
    description:
      "Allows the computer to fire a number of turrets per round equal to the listed number or give a positive DM to an attack equal to the listed number.",
    maxLevel: 5,
    levels: [
      { level: 1, bandwidth: 5, cost: 2, tl: 9 },
      { level: 2, bandwidth: 10, cost: 4, tl: 10 },
      { level: 3, bandwidth: 15, cost: 6, tl: 11 },
      { level: 4, bandwidth: 20, cost: 8, tl: 12 },
      { level: 5, bandwidth: 25, cost: 10, tl: 13 },
    ],
  },
  // Auto-Repair with proper levels
  {
    id: "autoRepair",
    name: "Auto-Repair",
    category: "Automation",
    tl: 10,
    bandwidth: 10,
    cost: 5,
    description:
      "The computer can make a number of repair attempts per round equal to the listed number. Requires repair drones.",
    maxLevel: 2,
    levels: [
      { level: 1, bandwidth: 10, cost: 5, tl: 10 },
      { level: 2, bandwidth: 20, cost: 10, tl: 12 },
    ],
  },
  // Library
  {
    id: "library",
    name: "Library",
    category: "Basic",
    tl: 8,
    bandwidth: 0,
    cost: 0,
    description: "Contains a wealth of data on numerous subjects.",
  },
  // Ship-based Computer Software
  {
    id: "interface",
    name: "Interface",
    category: "Basic",
    tl: 7,
    bandwidth: 0,
    cost: 0,
    description: "Displays data.",
  },
  {
    id: "intelligentInterface",
    name: "Intelligent Interface",
    category: "Basic",
    tl: 11,
    bandwidth: 1,
    cost: 0.0001,
    description:
      "Artificial intelligence allows voice control and displays data intelligently. Required for using Expert software.",
  },
  // Security with proper levels
  {
    id: "security",
    name: "Security",
    category: "Security",
    tl: 8,
    bandwidth: 0,
    cost: 0,
    description:
      "Security software packages defend against intrusion. Security/0 requires an Average (8+) Electronics (computers) check to successfully bypass.",
    maxLevel: 3,
    levels: [
      { level: 0, bandwidth: 0, cost: 0, tl: 8 },
      { level: 1, bandwidth: 1, cost: 0.2, tl: 10 },
      { level: 2, bandwidth: 2, cost: 1, tl: 11 },
      { level: 3, bandwidth: 3, cost: 20, tl: 12 },
    ],
  },
  // Intrusion with proper levels
  {
    id: "intrusion",
    name: "Intrusion",
    category: "Security",
    tl: 10,
    bandwidth: 1,
    cost: 1,
    description:
      "Intrusion software packages aid hacking attempts, giving a bonus equal to their Bandwidth. Intrusion software is often illegal.",
    maxLevel: 4,
    levels: [
      { level: 1, bandwidth: 1, cost: 1, tl: 10 },
      { level: 2, bandwidth: 2, cost: 10, tl: 11 },
      { level: 3, bandwidth: 3, cost: 100, tl: 13 },
      { level: 4, bandwidth: 4, cost: 1000, tl: 15 },
    ],
  },
  // Expert with proper levels
  {
    id: "expert",
    name: "Expert",
    category: "AI",
    tl: 11,
    bandwidth: 1,
    cost: 0.001,
    description:
      "Expert software packages mimic skills. A Traveller using Expert may make a skill check as if they had the skill at the software's Bandwidth -1.",
    maxLevel: 3,
    levels: [
      { level: 1, bandwidth: 1, cost: 0.001, tl: 11 },
      { level: 2, bandwidth: 2, cost: 0.01, tl: 12 },
      { level: 3, bandwidth: 3, cost: 0.1, tl: 13 },
    ],
  },
  // Translator with proper levels
  {
    id: "translator",
    name: "Translator",
    category: "AI",
    tl: 9,
    bandwidth: 0,
    cost: 0.00005,
    description:
      "Translators are specialised Expert packages that only have Language skills.",
    maxLevel: 1,
    levels: [
      { level: 0, bandwidth: 0, cost: 0.00005, tl: 9 },
      { level: 1, bandwidth: 1, cost: 0.0005, tl: 10 },
    ],
  },
  {
    id: "database",
    name: "Database",
    category: "Basic",
    tl: 7,
    bandwidth: 0,
    cost: 0.00001,
    description:
      "A database is a large store of information on a topic that can be searched with an Electronics (computers) check or using an Agent.",
    maxLevel: 4,
    levels: [
      { level: 0, bandwidth: 0, cost: 0.00001, tl: 7, description: "Basic database (Cr10)" },
      { level: 1, bandwidth: 0, cost: 0.0001, tl: 7, description: "Standard database (Cr100)" },
      { level: 2, bandwidth: 0, cost: 0.001, tl: 7, description: "Advanced database (Cr1,000)" },
      { level: 3, bandwidth: 0, cost: 0.01, tl: 7, description: "Comprehensive database (Cr10,000)" },
    ],
  },
  // Agent with proper levels
  {
    id: "agent",
    name: "Agent",
    category: "AI",
    tl: 11,
    bandwidth: 0,
    cost: 0.0005,
    description:
      "Agent packages have an Electronics (computers) skill equal to their Bandwidth and can carry out tasks assigned to them with a modicum of intelligence.",
    maxLevel: 3,
    levels: [
      { level: 0, bandwidth: 0, cost: 0.0005, tl: 11 },
      { level: 1, bandwidth: 1, cost: 0.002, tl: 12 },
      { level: 2, bandwidth: 2, cost: 0.1, tl: 13 },
      { level: 3, bandwidth: 3, cost: 0.25, tl: 14 },
    ],
  },
  // Intellect with proper levels (ship-based version)
  {
    id: "intellectAdvanced",
    name: "Intellect",
    category: "AI",
    tl: 12,
    bandwidth: 2,
    cost: 0.002,
    description:
      "Intellect is an improved Agent, which can use Expert systems.",
    maxLevel: 3,
    levels: [
      { level: 1, bandwidth: 2, cost: 0.002, tl: 12 },
      { level: 2, bandwidth: 3, cost: 0.05, tl: 13 },
      { level: 3, bandwidth: 4, cost: 0.2, tl: 14 },
    ],
  },
  // Advanced Fire Control
  {
    id: "advancedFireControl",
    name: "Advanced Fire Control",
    category: "Combat",
    tl: 10,
    bandwidth: 15,
    cost: 12,
    description:
      "All weapons gain a DM to their attack rolls equal to the package's score. Does not stack with Fire Control.",
    maxLevel: 3,
    levels: [
      { level: 1, bandwidth: 15, cost: 12, tl: 10 },
      { level: 2, bandwidth: 25, cost: 15, tl: 12 },
      { level: 3, bandwidth: 30, cost: 18, tl: 14 },
    ],
  },
  // Anti-Hijack
  {
    id: "antiHijack",
    name: "Anti-Hijack",
    category: "Security",
    tl: 11,
    bandwidth: 2,
    cost: 6,
    description:
      "Monitors airlocks and critical areas for unauthorized access. Applies a negative DM to unauthorized entry checks.",
    maxLevel: 3,
    levels: [
      { level: 1, bandwidth: 2, cost: 6, tl: 11 },
      { level: 2, bandwidth: 10, cost: 8, tl: 12 },
      { level: 3, bandwidth: 15, cost: 10, tl: 13 },
    ],
  },
  // Battle Network
  {
    id: "battleNetwork",
    name: "Battle Network",
    category: "Fleet",
    tl: 12,
    bandwidth: 5,
    cost: 5,
    description: "Enables sensor hand-off to all ships within specified range.",
    maxLevel: 2,
    levels: [
      { level: 1, bandwidth: 5, cost: 5, tl: 12 },
      { level: 2, bandwidth: 10, cost: 10, tl: 14 },
    ],
  },
  // Battle System
  {
    id: "battleSystem",
    name: "Battle System",
    category: "Fleet",
    tl: 9,
    bandwidth: 5,
    cost: 18,
    description:
      "Provides a DM to Tactics (naval) checks equal to the package's score.",
    maxLevel: 3,
    levels: [
      { level: 1, bandwidth: 5, cost: 18, tl: 9 },
      { level: 2, bandwidth: 10, cost: 24, tl: 12 },
      { level: 3, bandwidth: 15, cost: 36, tl: 15 },
    ],
  },
  // Broad Spectrum EW
  {
    id: "broadSpectrumEW",
    name: "Broad Spectrum EW",
    category: "EW",
    tl: 13,
    bandwidth: 12,
    cost: 14,
    description:
      "Automatically performs electronic warfare actions against all enemy missile salvoes within Long range.",
  },
  // Conscious Intelligence
  {
    id: "consciousIntelligence",
    name: "Conscious Intelligence",
    category: "AI",
    tl: 16,
    bandwidth: 40,
    cost: 25,
    description:
      "A fully sentient digital being, treated as a computer-bound Traveller with INT 15 and EDU 15.",
    maxLevel: 3,
    levels: [
      { level: 1, bandwidth: 40, cost: 25, tl: 16 },
      { level: 2, bandwidth: 25, cost: 20, tl: 17 },
      { level: 3, bandwidth: 10, cost: 15, tl: 18 },
    ],
  },
  // Electronic Warfare
  {
    id: "electronicWarfare",
    name: "Electronic Warfare",
    category: "EW",
    tl: 10,
    bandwidth: 10,
    cost: 15,
    description:
      "Provides a DM to Electronics (sensors) checks equal to the package's score.",
    maxLevel: 3,
    levels: [
      { level: 1, bandwidth: 10, cost: 15, tl: 10 },
      { level: 2, bandwidth: 15, cost: 18, tl: 13 },
      { level: 3, bandwidth: 20, cost: 24, tl: 15 },
    ],
  },
  // Launch Solution
  {
    id: "launchSolution",
    name: "Launch Solution",
    category: "Combat",
    tl: 8,
    bandwidth: 5,
    cost: 10,
    description:
      "Optimizes missile and torpedo attacks, granting a DM to all missile and torpedo salvo attack rolls.",
    maxLevel: 3,
    levels: [
      { level: 1, bandwidth: 5, cost: 10, tl: 8 },
      { level: 2, bandwidth: 10, cost: 12, tl: 10 },
      { level: 3, bandwidth: 15, cost: 16, tl: 12 },
    ],
  },
  // Point Defence
  {
    id: "pointDefence",
    name: "Point Defence",
    category: "Combat",
    tl: 9,
    bandwidth: 12,
    cost: 8,
    description:
      "Allows point defence batteries to defend any ship within Close (or Short) range.",
    maxLevel: 2,
    levels: [
      { level: 1, bandwidth: 12, cost: 8, tl: 9 },
      { level: 2, bandwidth: 15, cost: 12, tl: 12 },
    ],
  },
  // Screen Optimiser
  {
    id: "screenOptimiser",
    name: "Screen Optimiser",
    category: "Defence",
    tl: 10,
    bandwidth: 10,
    cost: 5,
    description:
      "Automatically configures screens to best degrade enemy attacks.",
  },
  // Virtual Crew
  {
    id: "virtualCrew",
    name: "Virtual Crew",
    category: "Automation",
    tl: 10,
    bandwidth: 5,
    cost: 1,
    description:
      "Can replace up to five pilots, gunners or sensor operators. Requires +1 Bandwidth for every five crew beyond the first five.",
    maxLevel: 2,
    levels: [
      { level: 0, bandwidth: 5, cost: 1, tl: 10 },
      { level: 1, bandwidth: 10, cost: 5, tl: 13 },
      { level: 2, bandwidth: 15, cost: 10, tl: 15 },
    ],
  },
  // Virtual Gunner
  {
    id: "virtualGunner",
    name: "Virtual Gunner",
    category: "Automation",
    tl: 9,
    bandwidth: 5,
    cost: 1,
    description:
      "Can replace any number of gunners. Requires +1 Bandwidth for every 10 gunners beyond the first 10.",
    maxLevel: 2,
    levels: [
      { level: 0, bandwidth: 5, cost: 1, tl: 9 },
      { level: 1, bandwidth: 10, cost: 5, tl: 12 },
      { level: 2, bandwidth: 15, cost: 10, tl: 15 },
    ],
  },
];
